import { ComponentProps, useEffect } from 'react';
import { TokenAccessor } from '../../types/API';
import { Module, buildBookWidgetsUrl } from '../../util/buildBookWidgetsUrl';
import {
  AssignmentLtiUserMaterial,
  ExerciseModalTranslations,
  LtiExerciseMaterial,
  ScorableAssignmentLtiUserMaterial,
} from './types';
import { isAssignmentLTI } from './utils';
import useAnalytics from '../../hooks/useAnalytics';
import { LtiModal, Platform } from '../lti-modal';
import { ANALYTICS_EVENT_TYPES, AnalyticsData } from '../../hooks/constants/Analytics';

type StartLTIExerciseProps = {
  material: LtiExerciseMaterial;
  module: Module;
  platformName: Platform;
  studioLanguage: string;
  ltipsUrl: string;
  onClose: () => void;
  tokenAccessor: TokenAccessor;
  translations: ExerciseModalTranslations;
  id: string;
  isOwner: boolean;
  analyticsData?: AnalyticsData;
  studioGroup: string;
  headerProps: ComponentProps<typeof LtiModal>['headerProps'];
};

function transformAssignmentMaterial(
  material: AssignmentLtiUserMaterial | ScorableAssignmentLtiUserMaterial,
  isOwner: boolean,
): Parameters<typeof buildBookWidgetsUrl>[0]['ltiUserMaterial'] {
  if (isOwner) {
    const { assignment: omitAssignment, ...materialWithoutAssignment } = material;

    return {
      ...materialWithoutAssignment,
    };
  }

  return {
    ...material,
    assignmentId: material.assignment.id,
  };
}

function getMaterialAudience(material: LtiExerciseMaterial): Parameters<typeof buildBookWidgetsUrl>[0]['audience'] {
  if (!material.assignment && !material.share) return undefined;

  if (isAssignmentLTI(material)) {
    return {
      groupName: material.assignment.groupName,
      schoolName: material.assignment.schoolName,
    };
  }

  return {
    groupName: material.share?.groupName,
    schoolName: material.share?.schoolName,
  };
}

export default function StartLTIExercise({
  id,
  material,
  module,
  platformName,
  studioLanguage,
  ltipsUrl,
  onClose,
  tokenAccessor,
  headerProps,
  translations,
  isOwner,
  analyticsData,
  studioGroup,
}: StartLTIExerciseProps): JSX.Element {
  const url = buildBookWidgetsUrl({
    platformName,
    studioLanguage,
    ltipsUrl,
    ltiUserMaterial: isAssignmentLTI(material) ? transformAssignmentMaterial(material, isOwner) : material,
    module,
    audience: getMaterialAudience(material),
  });
  const analytics = useAnalytics();

  useEffect(() => {
    const captureEvent = async (): Promise<void> => {
      const token = await tokenAccessor.tokenGetter();

      if (analyticsData && token) {
        analytics.capture(
          {
            eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_ACCESSED,
            objectId: material.id,
            analyticsData,
          },
          token,
        );
      }
    };

    captureEvent();
  }, [analytics, analyticsData, tokenAccessor, material]);

  return (
    <LtiModal
      id={id}
      loginUrl={url}
      onClose={onClose}
      tokenAccessor={tokenAccessor}
      studioGroup={studioGroup}
      headerProps={headerProps}
      aria-label={translations.ariaLabel}
    />
  );
}
